import { motion } from 'framer-motion';
import { useT } from '@/lib/i18n';
import {
  Sparkles,
  Search,
  Route,
  Rocket,
  ShieldCheck,
  Target,
  Workflow,
  RefreshCw,
  Gauge,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export function ConsultingExpertise() {
  const t = useT();
  const expertiseAreas = [
    {
      icon: Sparkles,
      title: t('KI als Wettbewerbsvorteil', 'AI as a competitive advantage'),
      description: t(
        'Wir zeigen Ihnen, wie Künstliche Intelligenz, Generative AI und KI-Agenten Ihr Unternehmen transformieren. Ob ChatGPT-Integration, intelligente Assistenten oder maßgeschneiderte KI-Lösungen – nicht als Hype, sondern als echtes Werkzeug für Wachstum, Effizienz und digitale Transformation im Mittelstand.',
        'We show you how artificial intelligence, generative AI and AI agents can transform your company. Whether ChatGPT integration, intelligent assistants or custom AI solutions — not as a hype, but as a real tool for growth, efficiency and digital transformation in the mid-market.',
      ),
    },
    {
      icon: Search,
      title: t('KI-Potenziale identifizieren', 'Identify AI potential'),
      description: t(
        'Systematische Analyse Ihrer Geschäftsprozesse: Wo bringen KI-Agenten, Predictive Maintenance oder Datenanalyse den größten Hebel? Wir finden die Anwendungsfälle mit dem höchsten ROI für Ihr produzierendes Unternehmen.',
        'A systematic analysis of your business processes: where do AI agents, predictive maintenance or data analysis create the biggest leverage? We find the use cases with the highest ROI for your manufacturing company.',
      ),
    },
    {
      icon: Target,
      title: t('Anwendungsfälle bewerten & priorisieren', 'Evaluate & prioritise use cases'),
      description: t(
        'Nicht jeder KI-Anwendungsfall lohnt sich. Wir bewerten Machbarkeit, Nutzen und Risiken von Automatisierung, Predictive Quality und ERP-Integration und priorisieren mit klarer Roadmap.',
        'Not every AI use case is worth it. We evaluate feasibility, value and risk of automation, predictive quality and ERP integration and prioritise with a clear roadmap.',
      ),
    },
    {
      icon: Route,
      title: t('KI-Strategie & Roadmap', 'AI strategy & roadmap'),
      description: t(
        'Von der Ambition zur Umsetzung: Wir entwickeln Ihre individuelle KI-Strategie mit klaren Meilensteinen und realistischen Zielen. Inklusive Fördermittelberatung für KI-Projekte im Mittelstand.',
        'From ambition to execution: we develop your individual AI strategy with clear milestones and realistic goals — including funding advice for AI projects in the mid-market.',
      ),
    },
    {
      icon: Rocket,
      title: t('Implementierung & Softwareentwicklung', 'Implementation & software development'),
      description: t(
        'Von der Idee zur produktiven Lösung: ob KI-Pilotprojekt, KI-Agenten-Integration, Low-Code-Automatisierung oder Hybrid-Ansatz. Unser KI Readiness Check zeigt, wo Sie starten sollten – wir entwickeln, begleiten und bringen live. Auch ohne eigene IT-Abteilung.',
        'From idea to productive solution: AI pilot, AI agent integration, low-code automation or hybrid approach. Our AI Readiness Check shows where to start — we build, support and ship. Even without an in-house IT department.',
      ),
    },
    {
      icon: Workflow,
      title: t('Prozessautomatisierung', 'Process automation'),
      description: t(
        'Von der Angebotserstellung bis zur Qualitätskontrolle: Wir automatisieren wiederkehrende Aufgaben mit KI-gestützten Workflows, RPA und intelligenter Dokumentenverarbeitung für KMU.',
        'From quoting to quality control: we automate recurring tasks with AI-powered workflows, RPA and intelligent document processing for SMEs.',
      ),
    },
    {
      icon: RefreshCw,
      title: t('Change Management', 'Change management'),
      description: t(
        'Veränderung gelingt nur mit den Menschen. Wir begleiten Ihr Team durch Digitalisierung, digitale Transformation und KI-Einführung, praxisnah und auf Augenhöhe.',
        'Change only works with people. We guide your team through digitalisation, digital transformation and AI adoption — pragmatic and on eye level.',
      ),
    },
    {
      icon: Gauge,
      title: t('Lean Production & Shopfloor', 'Lean production & shopfloor'),
      description: t(
        'Schlanke Fertigung mit KI-Unterstützung: Predictive Maintenance zur vorausschauenden Wartung, Durchlaufzeiten verkürzen und Shopfloor-Prozesse datenbasiert optimieren. Industrie 4.0 in der Praxis.',
        'Lean manufacturing with AI support: predictive maintenance, shorter cycle times and data-driven optimisation of shopfloor processes. Industry 4.0 in practice.',
      ),
    },
    {
      icon: ShieldCheck,
      title: t('EU AI Act, NIS2 & Compliance', 'EU AI Act, NIS2 & compliance'),
      description: t(
        'Wir helfen Ihnen, KI verantwortungsvoll und rechtskonform einzusetzen. Inklusive Risikobewertung gemäß EU AI Act, NIS2-Readiness, DORA-Compliance und ISO 42001 Vorbereitung. DSGVO-konforme KI-Lösungen sind bei uns Standard – mit transparenten AV-Verträgen und fairen Vertragsbedingungen.',
        'We help you deploy AI responsibly and legally. Including risk classification under the EU AI Act, NIS2 readiness, DORA compliance and ISO 42001 preparation. GDPR-compliant AI solutions are standard with us — with transparent data processing agreements and fair contract terms.',
      ),
    },
  ];
  return (
    <section className="py-20 md:py-28" aria-label={t('Kompetenzfelder KI-Beratung und Digitalisierung', 'AI consulting and digitalisation expertise')}>
      <div className="container mx-auto px-6">
        <motion.div className="text-center max-w-2xl mx-auto mb-16" {...fadeUp} transition={{ duration: 0.5 }}>
          <h2 className="text-3xl md:text-4xl font-bold">
            {t('Unsere ', 'Our ')}<span className="text-primary">{t('Kompetenzfelder', 'expertise')}</span>
          </h2>
          <p className="mt-4 text-muted-foreground">
            {t(
              'Fundiertes Know-how aus Zertifizierung, Industriepraxis und über 20 Jahren Erfahrung – gebündelt für Ihren KI-Erfolg.',
              'Deep know-how from certification, industrial practice and 20+ years of experience — combined for your AI success.',
            )}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {expertiseAreas.map((item, index) => (
            <motion.article
              key={item.title}
              className="p-6 rounded-2xl bg-secondary/30 border border-border/40 hover:border-primary/30 transition-colors group"
              {...fadeUp}
              transition={{ duration: 0.5, delay: index * 0.08 }}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <item.icon className="w-6 h-6 text-primary" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
